<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import { useMapGetter } from 'dashboard/composables/store';
import { useInternalChatStore } from 'dashboard/stores/internalChat';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import AgentPresenceDot from './AgentPresenceDot.vue';

const { t } = useI18n();
const store = useInternalChatStore();
const { activeThreadId } = storeToRefs(store);

const currentUserId = useMapGetter('getCurrentUserID');
const agents = useMapGetter('agents/getAgents');
const myTeams = useMapGetter('teams/getMyTeams');

const showNewMenu = ref(false);
const search = ref('');

// DM = outro participante; team = nome do time.
const otherParticipant = thread => {
  if (thread.thread_type === 'team') return null;
  return (thread.participants || []).find(p => p.id !== currentUserId.value);
};

const threadLabel = thread => {
  if (thread.thread_type === 'team') {
    const team = (myTeams.value || []).find(tm => tm.id === thread.team_id);
    return team ? team.name : t('INTERNAL_CHAT.THREAD.TEAM_CHANNEL');
  }
  const other = otherParticipant(thread);
  return other ? other.name : t('INTERNAL_CHAT.THREAD.DIRECT');
};

const threadSubtitle = thread =>
  thread.thread_type === 'team'
    ? t('INTERNAL_CHAT.THREAD.TEAM_CHANNEL')
    : t('INTERNAL_CHAT.THREAD.DIRECT');

const formatTime = ts => {
  if (!ts) return '';
  const date = new Date(ts * 1000);
  const now = new Date();
  const sameDay = date.toDateString() === now.toDateString();
  return sameDay
    ? date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : date.toLocaleDateString([], { day: '2-digit', month: '2-digit' });
};

const availableAgents = computed(() => {
  const q = search.value.trim().toLowerCase();
  return (agents.value || [])
    .filter(a => a.id !== currentUserId.value)
    .filter(a => !q || (a.name || '').toLowerCase().includes(q));
});

const onSelect = id => store.selectThread(id);

const onOpenDirect = async agentId => {
  showNewMenu.value = false;
  search.value = '';
  await store.openDirect(agentId);
};

const onOpenTeam = async teamId => {
  showNewMenu.value = false;
  await store.openTeam(teamId);
};
</script>

<template>
  <div
    class="flex flex-col h-full border-r w-80 border-n-weak shrink-0 bg-n-surface-1"
  >
    <!-- Header da lista -->
    <div
      class="flex justify-between items-center px-4 border-b h-14 border-n-weak shrink-0"
    >
      <h2 class="text-base font-semibold text-n-slate-12">
        {{ t('INTERNAL_CHAT.THREAD.LIST_TITLE') }}
      </h2>
      <button
        type="button"
        class="flex justify-center items-center rounded-lg transition-colors size-8 text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
        :class="{ 'bg-n-alpha-2 text-n-slate-12': showNewMenu }"
        :aria-label="t('INTERNAL_CHAT.NEW_DM')"
        @click="showNewMenu = !showNewMenu"
      >
        <span class="i-lucide-pen-line size-4" />
      </button>
    </div>

    <!-- Seletor de nova conversa -->
    <div
      v-if="showNewMenu"
      class="flex flex-col border-b max-h-72 border-n-weak shrink-0"
    >
      <div class="p-2">
        <div class="relative">
          <span
            class="absolute i-lucide-search top-1/2 ltr:left-2.5 rtl:right-2.5 -translate-y-1/2 size-3.5 text-n-slate-10"
          />
          <input
            v-model="search"
            type="text"
            :placeholder="t('INTERNAL_CHAT.NEW_DM')"
            class="py-1.5 w-full text-sm rounded-lg border ltr:pl-8 rtl:pr-8 bg-n-alpha-1 border-n-weak text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
          />
        </div>
      </div>
      <div class="overflow-y-auto flex-1 px-2 pb-2">
        <button
          v-for="agent in availableAgents"
          :key="`agent-${agent.id}`"
          type="button"
          class="flex gap-2.5 items-center px-2 py-2 w-full text-sm text-left rounded-lg hover:bg-n-alpha-2 text-n-slate-12"
          @click="onOpenDirect(agent.id)"
        >
          <Avatar :name="agent.name" :src="agent.thumbnail" :size="28" />
          <span class="truncate">{{ agent.name }}</span>
        </button>

        <template v-if="(myTeams || []).length">
          <p
            class="px-2 pt-3 pb-1 text-xs font-medium tracking-wide uppercase text-n-slate-10"
          >
            {{ t('INTERNAL_CHAT.NEW_TEAM_CHANNEL') }}
          </p>
          <button
            v-for="team in myTeams"
            :key="`team-${team.id}`"
            type="button"
            class="flex gap-2.5 items-center px-2 py-2 w-full text-sm text-left rounded-lg hover:bg-n-alpha-2 text-n-slate-12"
            @click="onOpenTeam(team.id)"
          >
            <span
              class="flex justify-center items-center rounded-full size-7 bg-n-iris-3 text-n-iris-11"
            >
              <span class="i-lucide-users size-3.5" />
            </span>
            <span class="truncate">{{ team.name }}</span>
          </button>
        </template>
      </div>
    </div>

    <!-- Lista de conversas -->
    <ul class="flex overflow-y-auto flex-col flex-1 gap-0.5 p-2 m-0 list-none">
      <li v-for="thread in store.sortedThreads" :key="thread.id">
        <button
          type="button"
          class="flex gap-3 items-center px-3 py-2.5 w-full text-left rounded-xl transition-colors"
          :class="
            thread.id === activeThreadId ? 'bg-n-alpha-2' : 'hover:bg-n-alpha-1'
          "
          @click="onSelect(thread.id)"
        >
          <span class="relative shrink-0">
            <span
              v-if="thread.thread_type === 'team'"
              class="flex justify-center items-center rounded-full size-10 bg-n-iris-3 text-n-iris-11"
            >
              <span class="i-lucide-users size-5" />
            </span>
            <Avatar
              v-else
              :name="threadLabel(thread)"
              :src="otherParticipant(thread)?.avatar_url"
              :size="40"
            />
            <AgentPresenceDot
              v-if="thread.thread_type !== 'team' && otherParticipant(thread)"
              :user-id="otherParticipant(thread).id"
              class="absolute -right-0.5 -bottom-0.5 ring-2 ring-n-surface-1"
            />
          </span>
          <span class="flex-1 min-w-0">
            <span class="flex gap-2 justify-between items-center">
              <span class="text-sm font-medium truncate text-n-slate-12">
                {{ threadLabel(thread) }}
              </span>
              <span class="text-xs shrink-0 text-n-slate-10">
                {{ formatTime(thread.last_activity_at) }}
              </span>
            </span>
            <span class="flex gap-2 justify-between items-center mt-0.5">
              <span class="text-xs truncate text-n-slate-11">
                {{ threadSubtitle(thread) }}
              </span>
              <span
                v-if="thread.unread_count"
                class="flex justify-center items-center px-1.5 min-w-5 h-5 text-xs font-semibold text-white rounded-full shrink-0 bg-n-brand"
              >
                {{ thread.unread_count }}
              </span>
            </span>
          </span>
        </button>
      </li>
      <li
        v-if="!store.sortedThreads.length"
        class="px-4 py-10 text-sm leading-relaxed text-center text-n-slate-10"
      >
        {{ t('INTERNAL_CHAT.THREAD.EMPTY') }}
      </li>
    </ul>
  </div>
</template>
